import { c as _c } from "react/compiler-runtime";
import { type ConfigReq, useMutationReportAnalytics } from 'src/home/api';
import { useCallback } from 'react';
import { type UseMutateFunction } from '@tanstack/react-query';
import { type ReportingBaseTotalsGroupedByDateResp, type ReportingBaseTotalsResp, type ReportingBaseReportResp, type Filter, type ReportType, type AnalyticsReqField, type ChannelGroupingPerformanceStandardReportResp, type ChannelGroupingPerformanceTotalsResp, type ChannelGroupingPerformanceTotalsGroupedByDateResp, type ReportingBaseConversionEventResp } from 'shared/api/analytics';
import { type ClientError } from 'shared/api/fetch';
import { type CurrencyCodes } from 'shared/constants/currency';
import { HttpStatusCodes } from 'shared/httpStatus';
export interface GetHomePageData {
  currentTimePeriod: [string, string];
  previousTimePeriod: [string, string];
  clientId: number;
  currency: CurrencyCodes;
  reportId: number;
  conversionNameFilters?: string[];
  channelAndMarketFilters?: Filter[];
  customFilters?: Filter[];
  reportType: ReportType;
  reportAnalyticsVariables: {
    fields: AnalyticsReqField[];
    groupBy: string[];
  };
  totalVariables: {
    fields: AnalyticsReqField[];
  };
}
type ReportAnalytics = ReportingBaseReportResp[] & ChannelGroupingPerformanceStandardReportResp[];
type ReportAnalyticsTotals = ReportingBaseTotalsResp & ChannelGroupingPerformanceTotalsResp;
type ReportAnalyticsTotalsGroupedByDate = ReportingBaseTotalsGroupedByDateResp[] & ChannelGroupingPerformanceTotalsGroupedByDateResp[];
export const useHomePageData = () => {
  const $ = _c(46);
  const {
    mutate: getReportingBaseReportAnalytics,
    isPending: isLoadingReportingBaseReportAnalytics,
    status: getReportingBaseReportAnalyticsStatus,
    isError: isErrorGettingReportingBaseReportAnalytics,
    error: getReportingBaseReportAnalyticsError,
    data: t0,
    reset: resetReportingBaseReportAnalytics
  } = useMutationReportAnalytics();
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const reportDataAnalytics = t1;
  const {
    mutate: getPreviousReportingBaseReportAnalytics,
    isPending: isLoadingPreviousReportingBaseReportAnalytics,
    status: getPreviousReportingBaseReportAnalyticsStatus,
    isError: isErrorGettingPreviousReportingBaseReportAnalytics,
    data: t2,
    reset: resetPreviousReportingBaseReportAnalytics
  } = useMutationReportAnalytics();
  let t3;
  if ($[2] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const previousDataAnalytics = t3;
  const {
    mutate: getTotalsGroupedByDate,
    isPending: isLoadingTotalsGroupedByDate,
    status: getTotalsGroupedByDateStatus,
    isError: isErrorGettingTotalsGroupedByDate,
    data: t4,
    reset: resetTotalsGroupedByDate
  } = useMutationReportAnalytics();
  let t5;
  if ($[4] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const totalsGroupedByDate = t5;
  const {
    mutate: getTotalsGroupedByPreviousDate,
    isPending: isLoadingTotalsGroupedByPreviousDate,
    status: getTotalsGroupedByPreviousDateStatus,
    isError: isErrorGettingTotalsGroupedByPreviousDate,
    data: t6,
    reset: resetTotalsGroupedByPreviousDate
  } = useMutationReportAnalytics();
  let t7;
  if ($[6] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const previousTotalsGroupedByDate = t7;
  const {
    mutate: getTotals,
    isPending: isLoadingTotalsData,
    status: getTotalsStatus,
    isError: isErrorGettingTotalsData,
    data: totalsData,
    reset: resetTotals
  } = useMutationReportAnalytics();
  const {
    mutate: getPreviousTotals,
    isPending: isLoadingRawPreviousReportingBaseReportAnalyticsTotals,
    status: getPreviousTotalsStatus,
    isError: isErrorGettingPreviousTotalsData,
    data: previousTotalsData,
    reset: resetPreviousTotals
  } = useMutationReportAnalytics();
  const {
    mutate: getFilterOptions,
    isPending: isLoadingFilterOptions,
    status: getFilterOptionsStatus,
    data: t8,
    reset: resetFilterOptions
  } = useMutationReportAnalytics();
  let t9;
  if ($[8] !== t8) {
    t9 = t8 === undefined ? [] : t8;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const filterOptionsData = t9;
  const {
    mutate: getConversionNamesAndSources,
    isPending: isLoadingConversionNamesAndSources,
    status: getConversionNamesAndSourcesStatus,
    data: conversionNameAndSources,
    isError: isConversionNamesAndSourcesError,
    error: conversionNamesAndSourcesError,
    reset: resetConversionNamesAndSources
  } = useMutationReportAnalytics();
  const getReportingBaseReportAnalyticsRequestIsIdle = getReportingBaseReportAnalyticsStatus === "idle";
  const getPreviousReportingBaseReportAnalyticsRequestIsIdle = getPreviousReportingBaseReportAnalyticsStatus === "idle";
  const getTotalsGroupedByDateRequestIsIdle = getTotalsGroupedByDateStatus === "idle";
  const getTotalsGroupedByPreviousDateRequestIsIdle = getTotalsGroupedByPreviousDateStatus === "idle";
  const getTotalsRequestIsIdle = getTotalsStatus === "idle";
  const getPreviousTotalsRequestIsIdle = getPreviousTotalsStatus === "idle";
  const isGetFilterOptionsRequestIdle = getFilterOptionsStatus === "idle";
  const isGetConversionNamesAndSourcesRequestIdle = getConversionNamesAndSourcesStatus === "idle";
  const isDataLoading = isLoadingReportingBaseReportAnalytics || isLoadingTotalsData || isLoadingTotalsGroupedByDate || isLoadingPreviousReportingBaseReportAnalytics || isLoadingTotalsGroupedByPreviousDate || isLoadingRawPreviousReportingBaseReportAnalyticsTotals;
  const isAnyRequestIdle = getReportingBaseReportAnalyticsRequestIsIdle || getTotalsRequestIsIdle || getTotalsGroupedByDateRequestIsIdle || getPreviousReportingBaseReportAnalyticsRequestIsIdle || getTotalsGroupedByPreviousDateRequestIsIdle || getPreviousTotalsRequestIsIdle;
  const isFilterOptionsLoaded = !isLoadingFilterOptions && !isGetFilterOptionsRequestIdle;
  const isDataLoaded = !isDataLoading && !isAnyRequestIdle && isFilterOptionsLoaded;
  const isDataError = isConversionNamesAndSourcesError || isErrorGettingReportingBaseReportAnalytics && isErrorGettingTotalsData && isErrorGettingTotalsGroupedByDate && isErrorGettingPreviousReportingBaseReportAnalytics && isErrorGettingTotalsGroupedByPreviousDate && isErrorGettingPreviousTotalsData;
  const isDataNotFoundError = getReportingBaseReportAnalyticsError?.status_code === HttpStatusCodes.NOT_FOUND || conversionNamesAndSourcesError?.status_code === HttpStatusCodes.NOT_FOUND;
  let t10;
  if ($[10] !== getPreviousReportingBaseReportAnalytics || $[11] !== getPreviousTotals || $[12] !== getReportingBaseReportAnalytics || $[13] !== getTotals || $[14] !== getTotalsGroupedByDate || $[15] !== getTotalsGroupedByPreviousDate) {
    t10 = t11 => {
      const {
        currentTimePeriod,
        previousTimePeriod,
        clientId,
        currency,
        reportId,
        customFilters: t12,
        reportType,
        reportAnalyticsVariables,
        totalVariables
      } = t11;
      const customFilters = t12 === undefined ? [] : t12;
      const sharedPayload = {
        clientId,
        targetCurrencyCode: currency,
        id: reportId.toString(),
        report: reportType,
        limit: 100000
      };
      const currentDateFilter = [{
        field: "date",
        operator: "BETWEEN",
        value: currentTimePeriod
      }];
      const previousDateFilter = [{
        field: "date",
        operator: "BETWEEN",
        value: previousTimePeriod
      }];
      getReportingBaseReportAnalytics({
        ...sharedPayload,
        ...reportAnalyticsVariables,
        filters: [...currentDateFilter, ...customFilters]
      });
      getPreviousReportingBaseReportAnalytics({
        ...sharedPayload,
        ...reportAnalyticsVariables,
        filters: [...previousDateFilter, ...customFilters]
      });
      getTotalsGroupedByDate({
        ...sharedPayload,
        fields: [...totalVariables.fields, {
          name: "date"
        }],
        filters: [...currentDateFilter, ...customFilters],
        groupBy: ["date"],
        orderBy: {
          fields: ["date"],
          direction: "ASC"
        }
      });
      getTotalsGroupedByPreviousDate({
        ...sharedPayload,
        fields: [...totalVariables.fields, {
          name: "date"
        }],
        filters: [...previousDateFilter, ...customFilters],
        groupBy: ["date"]
      });
      getTotals({
        ...sharedPayload,
        ...totalVariables,
        filters: [...currentDateFilter, ...customFilters]
      });
      getPreviousTotals({
        ...sharedPayload,
        ...totalVariables,
        filters: [...previousDateFilter, ...customFilters]
      });
    };
    $[10] = getPreviousReportingBaseReportAnalytics;
    $[11] = getPreviousTotals;
    $[12] = getReportingBaseReportAnalytics;
    $[13] = getTotals;
    $[14] = getTotalsGroupedByDate;
    $[15] = getTotalsGroupedByPreviousDate;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const getHomePageData = t10;
  let t11;
  if ($[17] !== resetConversionNamesAndSources || $[18] !== resetFilterOptions || $[19] !== resetPreviousReportingBaseReportAnalytics || $[20] !== resetPreviousTotals || $[21] !== resetReportingBaseReportAnalytics || $[22] !== resetTotals || $[23] !== resetTotalsGroupedByDate || $[24] !== resetTotalsGroupedByPreviousDate) {
    t11 = () => {
      resetReportingBaseReportAnalytics();
      resetPreviousReportingBaseReportAnalytics();
      resetTotals();
      resetPreviousTotals();
      resetTotalsGroupedByDate();
      resetTotalsGroupedByPreviousDate();
      resetFilterOptions();
      resetConversionNamesAndSources();
    };
    $[17] = resetConversionNamesAndSources;
    $[18] = resetFilterOptions;
    $[19] = resetPreviousReportingBaseReportAnalytics;
    $[20] = resetPreviousTotals;
    $[21] = resetReportingBaseReportAnalytics;
    $[22] = resetTotals;
    $[23] = resetTotalsGroupedByDate;
    $[24] = resetTotalsGroupedByPreviousDate;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const resetHomePageData = t11;
  const t12 = !isLoadingConversionNamesAndSources && !isGetConversionNamesAndSourcesRequestIdle && !isConversionNamesAndSourcesError;
  const t13 = totalsData?.[0];
  const t14 = previousTotalsData?.[0];
  let t15;
  if ($[26] !== conversionNameAndSources || $[27] !== filterOptionsData || $[28] !== getConversionNamesAndSources || $[29] !== getFilterOptions || $[30] !== getHomePageData || $[31] !== isConversionNamesAndSourcesError || $[32] !== isDataError || $[33] !== isDataLoaded || $[34] !== isDataNotFoundError || $[35] !== isFilterOptionsLoaded || $[36] !== isLoadingConversionNamesAndSources || $[37] !== previousDataAnalytics || $[38] !== previousTotalsGroupedByDate || $[39] !== reportDataAnalytics || $[40] !== resetHomePageData || $[41] !== t12 || $[42] !== t13 || $[43] !== t14 || $[44] !== totalsGroupedByDate) {
    t15 = {
      getHomePageData,
      resetHomePageData,
      getFilterOptions,
      getConversionNamesAndSources,
      filterOptionsData,
      isFilterOptionsLoaded,
      isConversionNamesAndSourcesLoaded: t12,
      isConversionNamesAndSourcesError,
      isLoadingConversionNamesAndSources,
      conversionNameAndSources,
      reportDataAnalytics,
      previousDataAnalytics,
      totalsGroupedByDate,
      previousTotalsGroupedByDate,
      totalsData: t13,
      previousTotalsData: t14,
      isDataLoaded,
      isDataError,
      isDataNotFoundError
    };
    $[26] = conversionNameAndSources;
    $[27] = filterOptionsData;
    $[28] = getConversionNamesAndSources;
    $[29] = getFilterOptions;
    $[30] = getHomePageData;
    $[31] = isConversionNamesAndSourcesError;
    $[32] = isDataError;
    $[33] = isDataLoaded;
    $[34] = isDataNotFoundError;
    $[35] = isFilterOptionsLoaded;
    $[36] = isLoadingConversionNamesAndSources;
    $[37] = previousDataAnalytics;
    $[38] = previousTotalsGroupedByDate;
    $[39] = reportDataAnalytics;
    $[40] = resetHomePageData;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = totalsGroupedByDate;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
};